import './index.scss'

import { useCallback, useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'

import { useAuth } from '@auth/AuthProvider'
import ErrorView from '@components/ErrorView'
import LoadingIndicator from '@components/LoadingIndicator'
import PageHeader from '@components/PageHeader'
import LaunchpadController from '@data/controller/LaunchpadController'
import {
  Launchpad,
  launchpadFromResponse,
} from '@interfaces/launchpad.interface'
import LaunchpadViewPreview from '@pages/v2/Launchpad/LaunchpadViewPreview'
import useLaunchpadLinks from './LinksEditor/useLaunchpadLinks'
import LaunchpadViewModal from '@pages/v2/Launchpad/Modal/LaunchpadViewModal'
import DeleteLaunchpadModal from '@pages/v2/Launchpad/Modal/DeleteLaunchpadModal'

import pencilIcon from 'url:@assets/icons/pencil.svg';
import trashIcon from 'url:@assets/icons/trash.svg';
import LinksEditorModal from './LinksEditor/LinksEditorModal'

export default function LaunchpadDetailsPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pathParams = useParams();
  const launchpadID = pathParams.launchpadID;
  const initialLaunchpad = (location.state as { launchpad?: Launchpad } | null)?.launchpad ?? null;

  const [launchpad, setLaunchpad] = useState<Launchpad | null>(initialLaunchpad);
  const [loading, setLoading] = useState(initialLaunchpad === null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const applyLaunchpad = useCallback((updatedLaunchpad: Launchpad) => {
    setLaunchpad(updatedLaunchpad);
  }, []);

  const linksEditor = useLaunchpadLinks(launchpad, applyLaunchpad);
  const [isLinkEditorOpen, setIsLinkEditorOpen] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    const loadLaunchpad = async () => {
      if (!auth.churchId || !launchpadID) return;

      setLoading(true);
      setError(null);

      const response = await LaunchpadController.fetchLaunchpad(auth.churchId, launchpadID, auth);

      if (isCancelled) {
        return;
      }

      const fetchedLaunchpad = response.error ? null : launchpadFromResponse(response.body);

      if (!fetchedLaunchpad) {
        setError(response.error || 'Failed to load this Launchpad.');
        setLaunchpad(null);
      } else {
        applyLaunchpad(fetchedLaunchpad);
      }

      setLoading(false);
      setHasLoaded(true);
    }

    loadLaunchpad();

    return () => {
      isCancelled = true;
    }
  }, [auth.churchId, auth.consumeRefreshToken, launchpadID, reloadKey]);

  if (!launchpadID) {
    return <Navigate to='/launchpad' replace />
  }

  return <div className='launchpad-details-page'>
    {loading && !launchpad ? <LoadingIndicator /> : null}

    {error && !launchpad ? <ErrorView
      error={error}
      buttonText='Retry'
      buttonAction={() => setReloadKey((currentKey) => currentKey + 1)} /> : null}

    {hasLoaded && !loading && !error && !launchpad ? <ErrorView
      error='Launchpad not found.'
      buttonText='Back to Launchpad'
      buttonAction={() => navigate('/launchpad')} /> : null}

    {launchpad ? <div>
      <PageHeader
        title={launchpad.metadata.attributes.name}
        subtitle={launchpad.metadata.attributes.description}
        backLocation='/launchpad'
        links={[
          {
            icon: pencilIcon,
            title: 'Edit',
            onClick: () => setIsEditModalOpen(true)
          },
          {
            icon: trashIcon,
            title: 'Delete',
            onClick: () => setIsDeleteModalOpen(true)
          }
        ]} />

      <LaunchpadViewModal
        key={`edit-launchpad-${launchpad.launchpadID}`}
        isOpen={isEditModalOpen}
        launchpad={launchpad}
        close={() => setIsEditModalOpen(false)}
        onSaved={(savedLaunchpad) => {
          applyLaunchpad(savedLaunchpad);
          setIsEditModalOpen(false);
        }}
      />

      <DeleteLaunchpadModal
        key={`delete-launchpad-${launchpad.launchpadID}`}
        isOpen={isDeleteModalOpen}
        launchpad={launchpad}
        close={() => setIsDeleteModalOpen(false)}
        onDeleted={() => {
          setIsDeleteModalOpen(false);
          navigate('/launchpad');
        }}
      />

      <LinksEditorModal
        isOpen={isLinkEditorOpen}
        editor={linksEditor}
        close={() => setIsLinkEditorOpen(false)}
      />

      {launchpad && <section className='launchpad-preview-section'>
        <h3>Preview</h3>
        <button type='button' className='edit-links-button' onClick={() => setIsLinkEditorOpen(true)}>
          Edit Links
        </button>
        <LaunchpadViewPreview launchpad={launchpad} />
      </section>}
    </div> : null}
  </div>
}
